// src/services/api.ts

import axios, { AxiosError } from 'axios';
import {
  API_BASE_URL,
  attachAuthInterceptors,
  authHeaders,
  activeWorkspaceManager,
  adminTokenManager,
  ensureFreshAccessToken,
  getApiTimezone,
  setApiTimezone,
  tokenManager,
} from './session';
// Attached here rather than in SignUpPage so both account-creation paths —
// password and OAuth — carry attribution from one place.
import { getAttribution } from '../utils/attribution';
import { trackSignupConversion } from '../utils/googleAds';
import { identifyUser, track } from '../utils/analytics';
import type {
  AuthResponse,
  User,
  Organization,
  BusinessLabel,
  Workspace,
  WorkspaceRole,
  WorkspaceMembership,
  DataSourceAccount,
  DataSource,
  AccountSummary,
  LoginForm,
  SignupStartPayload,
  SignupStartResponse,
  SignupVerifyEmailPayload,
  SignupVerifyEmailResponse,
  SignupCompletePayload,
  OAuthGooglePayload,
  OAuthGoogleResponse,
  OAuthApplePayload,
  OAuthAppleResponse,
  OAuthCompletePayload,
  OAuthNeedsProfileResponse,
  ImpersonationContext,
} from '../types';

/** Shape of GET /api/auth/me (Workspaces Layer 4). Active workspace + switcher.
 *  `organizations` are the active workspace's business labels — used for filters
 *  and explicit write targets, never as an ambient context. */
export interface MeResponse {
  user: User;
  workspace: Workspace | null;
  workspaceRole: WorkspaceRole | null;
  organizations: BusinessLabel[];
  memberships: WorkspaceMembership[];
  /** Non-null when a platform admin is signed in as this user. */
  impersonation?: ImpersonationContext | null;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  // Carries the httpOnly refresh cookie on sign-in, refresh and sign-out.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth headers, silent token refresh, sign-out policy and error toasts all live
// in ./session so every axios instance behaves identically.
attachAuthInterceptors(api);

// Re-exported so the rest of the app keeps importing session primitives from
// here; `./session` is the implementation, this is the front door.
export {
  API_BASE_URL,
  tokenManager,
  activeWorkspaceManager,
  adminTokenManager,
  setApiTimezone,
  getApiTimezone,
};
export { initSessionLifecycle } from './session';

/**
 * Common request headers — Authorization (when logged in) + X-Timezone — for
 * raw `fetch()` callers (SSE, multipart uploads) that can't go through an axios
 * instance. Async because those callers have no response interceptor to renew
 * an expired token for them, so freshness has to be guaranteed up front.
 */
export const getCommonHeaders = async (): Promise<Record<string, string>> => {
  await ensureFreshAccessToken();
  return authHeaders();
};

/** Discriminator for both `oauthGoogle` and `oauthApple` response branches. */
export const isNeedsProfile = (
  data: OAuthGoogleResponse | OAuthAppleResponse | undefined | null,
): data is OAuthNeedsProfileResponse => {
  return !!data && (data as OAuthNeedsProfileResponse).needsProfile === true;
};

/**
 * Identify the new account and record the conversion event.
 *
 * `workspace_id` rides along only on the OAuth response — the password signup
 * returns the new business, not the workspace. The identify() call is the join
 * key that actually matters: it makes the browser's distinct_id the user UUID,
 * so backend events land on the same person.
 */
function trackSignupComplete(data: AuthResponse, authProvider: string): void {
  if (data.user?.id) identifyUser(data.user.id);
  track('signup_complete', {
    auth_provider: authProvider,
    workspace_id: data.workspace?.id,
  });
}

// Auth API
export const authApi = {
  login: async (data: LoginForm & { totpCode?: string }): Promise<AuthResponse> => {
    const payload: Record<string, string> = {
      email: data.email,
      password: data.password,
    };
    if (data.totpCode) payload.totpCode = data.totpCode;

    const response = await api.post('/api/auth/login', payload);

    if (response.data.token) {
      tokenManager.set(response.data.token, response.data.expiresIn);
    }

    return response.data;
  },

  /**
   * Multi-step sign-up — step 1.
   * Server validates email syntax + MX + disposable, then issues a 6-digit code via Resend.
   * Does NOT create the user/org yet. Returns a `verificationId` that step 2 must echo back.
   */
  signupStart: async (data: SignupStartPayload): Promise<SignupStartResponse> => {
    const response = await api.post(
      '/api/auth/signup/start',
      { email: data.email },
      { silenceToast: true },
    );
    return response.data;
  },

  /**
   * Multi-step sign-up — step 2.
   * Submits the 6-digit code and trades it for a short-lived `signupToken` JWT
   * (purpose=signup) that step 3 must present.
   */
  signupVerifyEmail: async (
    data: SignupVerifyEmailPayload
  ): Promise<SignupVerifyEmailResponse> => {
    const response = await api.post(
      '/api/auth/signup/verify-email',
      { verificationId: data.verificationId, code: data.code },
      { silenceToast: true },
    );
    return response.data;
  },

  /**
   * Multi-step sign-up — step 3 (final).
   * Creates the org + user atomically and returns an authenticated session
   * (token stored here). 2FA is optional and enrolled later from settings.
   */
  signupComplete: async (data: SignupCompletePayload): Promise<AuthResponse> => {
    const response = await api.post(
      '/api/auth/signup/complete',
      {
        signupToken: data.signupToken,
        password: data.password,
        name: data.name,
        organizationName: data.organizationName,
        attribution: getAttribution() ?? undefined,
      },
      { silenceToast: true },
    );
    if (response.data.token) {
      tokenManager.set(response.data.token);
      trackSignupConversion();
      trackSignupComplete(response.data, 'email');
    }
    return response.data;
  },

  /**
   * Google OAuth — exchange a Google ID token (collected via Google Identity
   * Services in the browser) for either a session or an `oauthSignupToken`
   * if this is a new user that still needs to fill in name + business name.
   * Errors (collision, bad token) are surfaced inline by the auth pages.
   */
  oauthGoogle: async (data: OAuthGooglePayload): Promise<OAuthGoogleResponse> => {
    const response = await api.post(
      '/api/auth/oauth/google',
      { idToken: data.idToken },
      { silenceToast: true },
    );
    // Persist the session token only on the existing-user branch. The
    // needs-profile branch hands us a *signup* token that doesn't authenticate.
    if (!isNeedsProfile(response.data) && response.data?.token) {
      tokenManager.set(response.data.token);
    }
    return response.data;
  },

  /**
   * Apple OAuth — same shape as oauthGoogle. The optional `name` payload is
   * forwarded as-is (Apple includes it only on the very first sign-in for a
   * given Services ID + Apple ID pair). Backend treats it as a best-effort
   * suggestedName for the new-user profile step.
   */
  oauthApple: async (data: OAuthApplePayload): Promise<OAuthAppleResponse> => {
    const body: Record<string, unknown> = { idToken: data.idToken };
    if (data.name) body.name = data.name;
    const response = await api.post(
      '/api/auth/oauth/apple',
      body,
      { silenceToast: true },
    );
    if (!isNeedsProfile(response.data) && response.data?.token) {
      tokenManager.set(response.data.token);
    }
    return response.data;
  },

  /**
   * Second leg of the OAuth signup — turns the `oauthSignupToken` plus the
   * user-supplied name + business name into an actual account + session.
   * The response is always an authenticated AuthResponse.
   */
  oauthComplete: async (data: OAuthCompletePayload): Promise<AuthResponse> => {
    const response = await api.post(
      '/api/auth/oauth/complete',
      {
        oauthSignupToken: data.oauthSignupToken,
        name: data.name,
        organizationName: data.organizationName,
        attribution: getAttribution() ?? undefined,
      },
      { silenceToast: true },
    );
    if (response.data.token) {
      tokenManager.set(response.data.token);
      trackSignupConversion();
      trackSignupComplete(response.data, response.data.user?.authProvider ?? 'oauth');
    }
    return response.data;
  },

  getCurrentUser: async (): Promise<MeResponse> => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  /**
   * Switch the active workspace (Workspaces Layer 4). Persists the choice as the
   * X-Workspace-Id header source; the backend also records it as the identity's
   * last_active so it survives a fresh login on another device.
   */
  switchWorkspace: async (workspaceId: string): Promise<{
    workspace: Workspace;
    workspaceRole: WorkspaceRole;
    organizations: BusinessLabel[];
  }> => {
    const response = await api.post('/api/auth/switch', { workspaceId });
    activeWorkspaceManager.set(workspaceId);
    return response.data;
  },

  updateProfile: async (data: {
    firstName: string;
    lastName?: string | null;
    email: string;
    timezone?: string;
  }): Promise<{ user: User }> => {
    const response = await api.put('/api/auth/profile', data);
    return response.data;
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> => {
    const response = await api.put('/api/auth/password', data);
    return response.data;
  },

  /**
   * Revokes the session server-side and drops the refresh cookie, then clears
   * local state. The server call is awaited-but-forgiving: a failure must not
   * strand the user in a half-signed-out UI, and the local clear is what they
   * can see.
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout', null, { silenceToast: true });
    } catch {
      // Already expired or unreachable — signing out locally still stands.
    }
    tokenManager.remove();
    activeWorkspaceManager.remove();
    adminTokenManager.remove();
    window.dispatchEvent(new CustomEvent('auth:logout'));
  },

  validateInvitation: async (token: string): Promise<{
    valid: boolean;
    message?: string;
    organization_name?: string;
    invited_by_name?: string | null;
    role?: string;
    email?: string;
    expires_at?: string;
    /** True when the invited email already has a Zamino account — the accept UI
     *  shows "log in to accept" instead of the set-password form. */
    accountExists?: boolean;
  }> => {
    try {
      const response = await api.get(`/api/auth/invitations/${token}/validate`);
      return response.data;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 410) {
        const data = err.response.data as { message?: string } | undefined;
        return { valid: false, message: data?.message || 'This invitation is no longer valid.' };
      }
      throw err;
    }
  },

  /**
   * Public accept (new email → creates the account + session). For an email that
   * already has an account the backend returns `{ requiresLogin: true }` and no
   * token — the caller sends the user through login, then calls
   * `acceptInvitationAuthenticated`.
   */
  acceptInvitation: async (token: string, data: {
    firstName: string;
    lastName: string;
    password: string;
  }): Promise<AuthResponse & { requiresLogin?: boolean; organizationName?: string }> => {
    const response = await api.post(`/api/auth/invitations/${token}/accept`, data);
    if (response.data.token) {
      tokenManager.set(response.data.token);
    }
    return response.data;
  },

  /**
   * Accept as an already-authenticated identity (the "log in to accept" path for
   * an existing account). Attaches membership + grants to the current session's
   * identity; the caller then switches into the invited business's workspace.
   */
  acceptInvitationAuthenticated: async (token: string): Promise<{
    organization: Organization;
    workspaceId: string | null;
  }> => {
    const response = await api.post(`/api/auth/invitations/${token}/accept-authenticated`);
    return response.data;
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/reset-password', { token, newPassword });
    return response.data;
  },

  requestUnlock: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/request-unlock', { email });
    return response.data;
  },

  unlockAccount: async (token: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/unlock', { token });
    return response.data;
  },

  mfaBeginSetup: async (): Promise<{ secret: string; otpauthUrl: string; qrDataUrl: string }> => {
    const response = await api.post('/api/auth/mfa/setup');
    return response.data;
  },

  mfaVerifySetup: async (totpCode: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/mfa/verify', { totpCode });
    return response.data;
  },

  mfaDisable: async (totpCode: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/mfa/disable', { totpCode });
    return response.data;
  },

  mfaRequestReset: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/mfa/request-reset', { email });
    return response.data;
  },

  mfaCompleteReset: async (token: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/mfa/reset', { token });
    return response.data;
  },
};

// Integrations API - General integration endpoints
export const integrationsApi = {
  getAccounts: async (): Promise<{ accounts: DataSourceAccount[] }> => {
    const response = await api.get('/api/integrations/accounts');
    return response.data;
  },

  getDataSources: async (): Promise<{ dataSources: DataSource[] }> => {
    const response = await api.get<{ dataSources: DataSource[] }>('/api/integrations/connected');
    // Defensively strip backend fields we don't model on the frontend.
    const dataSources = (response.data.dataSources || [])
      .map((i): DataSource => ({
        id: i.id,
        name: i.name,
        type: i.type,
        institutionName: i.institutionName,
        healthStatus: i.healthStatus,
        syncEnabled: i.syncEnabled,
        lastSync: i.lastSync,
        createdAt: i.createdAt,
      }));
    return { dataSources };
  },

  syncDataSource: async (dataSourceId: string) => {
    const response = await api.post(`/api/integrations/data-sources/${dataSourceId}/sync`);
    return response.data;
  },

  removeDataSource: async (dataSourceId: string) => {
    const response = await api.delete(`/api/integrations/data-sources/${dataSourceId}`);
    return response.data;
  },

  getAccountSummary: async (): Promise<AccountSummary> => {
    const response = await api.get('/api/integrations/accounts/summary');
    return response.data;
  },
};

// Billing API (Pricing V2 — 4 tiers + pooled add-ons, billed per WORKSPACE)
export type BillingTier = 'free' | 'starter' | 'business' | 'scale';
/** Paid tiers that have a Stripe Checkout entry (Free has none). */
export type PaidTier = 'starter' | 'business' | 'scale';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled';

export interface SubscriptionResponse {
  tier: string;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  orgName: string | null;
  /** Tier this subscription drops to at `currentPeriodEnd`, if a downgrade is queued. */
  pendingTier: BillingTier | null;
}

/** Where a plan change landed: applied now, or queued for the renewal. */
export interface PlanChangeResponse {
  ok: boolean;
  effectiveAt: 'now' | 'period_end';
  effectiveDate: string | null;
  entitlements: EntitlementSummary;
}

export interface BillingLimitView {
  used: number;
  included: number;
  extra: number;
  limit: number;
  canAdd: boolean;
  addonAvailable: boolean;
  /** Per-unit add-on price (USD/mo), or null when the tier disallows add-ons. */
  unitPriceUsd: number | null;
  /** Scheduled add-on quantity at renewal (a deferred decrease), or null. */
  pendingExtra: number | null;
}

export interface EntitlementSummary {
  workspace_id: string;
  tier: BillingTier;
  subscription_status: SubscriptionStatus;
  features: {
    mcp: boolean;
    export: boolean;
    wikiSemantics: boolean;
    pulse: boolean;
    aiChat: boolean;
    compositeMetrics: boolean;
    granularPermissions: boolean;
    dashboards: boolean;
  };
  sources: BillingLimitView;
  seats: BillingLimitView;
  /** Free-tier daily MCP data-call usage; null on uncapped (paid) tiers. */
  mcpQuota: { used: number; limit: number; periodEndsAt: string } | null;
}

// Billing mutations own their error UX in the page (confirm modals + a single
// toast via handleApiError), so they silence the global interceptor to avoid a
// double/cascading toast — notably the expected 400 USE_CHECKOUT hop and the
// "billing not configured" case in envs without Stripe.
export const billingApi = {
  createCheckoutSession: async (tier: PaidTier): Promise<{ url: string }> => {
    const response = await api.post('/api/billing/checkout', { tier }, { silenceToast: true });
    return response.data;
  },
  /** Stripe-hosted: payment methods, invoices, cancellation. NOT plan changes. */
  createPortalSession: async (): Promise<{ url: string }> => {
    const response = await api.post('/api/billing/portal', undefined, { silenceToast: true });
    return response.data;
  },
  getSubscription: async (): Promise<SubscriptionResponse> => {
    const response = await api.get('/api/billing/subscription');
    return response.data;
  },
  getEntitlements: async (): Promise<EntitlementSummary> => {
    const response = await api.get('/api/billing/entitlements');
    return response.data;
  },
  /** Adjust a pooled add-on by a signed delta. Backend clamps extra >= 0. */
  adjustAddon: async (
    kind: 'sources' | 'seats',
    delta: number,
  ): Promise<{ ok: boolean; entitlements: EntitlementSummary }> => {
    const response = await api.post('/api/billing/addons', { kind, delta }, { silenceToast: true });
    return response.data;
  },
  /**
   * paid↔paid and →free. free/trial→paid throws 400 { code: 'USE_CHECKOUT' }.
   * Upgrades apply immediately; downgrades are queued for the renewal.
   */
  changePlan: async (tier: BillingTier): Promise<PlanChangeResponse> => {
    const response = await api.post('/api/billing/change-plan', { tier }, { silenceToast: true });
    return response.data;
  },
  /** Undo a queued downgrade and stay on the current plan. */
  cancelPlanChange: async (): Promise<{ ok: boolean; entitlements: EntitlementSummary }> => {
    const response = await api.post('/api/billing/change-plan/cancel', undefined, { silenceToast: true });
    return response.data;
  },
};

/**
 * Extract a human-readable message from an unknown thrown value.
 * Order of precedence: Axios error body `.error` → Axios message → Error.message → fallback.
 * Callers should pass the value from `catch (err)` directly; no manual narrowing needed.
 */
export const handleApiError = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
};

export default api;